import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, X } from "lucide-react";
import HintIcon from "../../../../shared/icons/HintIcon";
import InsuranceTerritoryWarningIcon from "../../../../shared/icons/InsuranceTerritoryWarningIcon";
import { File, Order } from "../../../../../services";
import { hasExcludedInsuranceTerritory } from "../../../../../utils/insuranceTerritories";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import InsuranceExclusionsModal from "./InsuranceExclusionsModal";
import InsuranceModal from "./InsuranceModal";
import style from "./InsuranceBlock.module.scss";

const formatInsuranceMoney = (value) => `${Number(value).toLocaleString("ru-RU").replace(/\s/g, " ")} ₸`;

export default function ArrangeInsuranceModal({ orderId, addressList, onClose, onSubmitted, onInvoiceUploaded }) {
  const { t } = useTranslation();
  const [cargoPrice, setCargoPrice] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(false);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [exclusionsOpen, setExclusionsOpen] = useState(false);
  const showTerritoryWarning = hasExcludedInsuranceTerritory(addressList);
  const fileInput = useRef(null);
  const uploadedInvoice = useRef(null);
  const submitting = useRef(false);
  const active = useRef(false);
  const validPrice =
    Number.isFinite(Number(cargoPrice)) && Number(cargoPrice) > 0 && Number(cargoPrice) <= 50000000;
  const quoteReady = validPrice && quote?.cargoPrice === Number(cargoPrice);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    setQuote(null);
    setQuoteError(false);
    if (!validPrice) return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await Order.calculateInsuranceSum(Number(cargoPrice));
        if (
          data?.insuranceSum == null ||
          !Number.isFinite(Number(data.insuranceSum)) ||
          Number(data.insuranceSum) < 0
        )
          throw new Error();
        if (current) setQuote({ cargoPrice: Number(cargoPrice), sum: Number(data.insuranceSum) });
      } catch {
        if (current) setQuoteError(true);
      }
    }, 600);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [cargoPrice, validPrice, quoteAttempt]);

  const selectInvoice = (file) => {
    if (submitting.current) return;
    setInvoice(file || null);
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || !quoteReady || !invoice) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      if (uploadedInvoice.current !== invoice) {
        const formData = new FormData();
        formData.append("file", invoice);
        formData.append("FileName", "Инвойс страхования");
        formData.append("name", "Инвойс страхования");
        formData.append("orderId", orderId);
        formData.append("type", 8);
        await File.addOrderDoc(formData);
        uploadedInvoice.current = invoice;
        if (!active.current) return;
        onInvoiceUploaded?.();
      }
      await Order.insureExpeditorOrder(orderId, { cargoPrice: Number(cargoPrice) });
      if (active.current) onSubmitted();
    } catch (error) {
      if (active.current) setError(error?.errorText || t("expeditorInsurance.submitError"));
    } finally {
      submitting.current = false;
      if (active.current) setBusy(false);
    }
  };

  return (
    <>
      <InsuranceModal title={t("expeditorInsurance.arrangeTitle")} onClose={onClose} busy={busy} arranging>
        {showTerritoryWarning && (
          <div className={style.territoryWarning} role="status">
            <InsuranceTerritoryWarningIcon />
            <div>
              <h3>{t("cargoInsurance.territoryNotice")}</h3>
              <p>{t("cargoInsurance.territoryCoverageDescription")}</p>
            </div>
          </div>
        )}
        <form onSubmit={submit} className={style.form}>
          <div className={style.details}>
            <CustomInput
              labelText={t("expeditorInsurance.cargoPrice")}
              placeholder={t("expeditorInsurance.pricePlaceholder")}
              inputType="number"
              prefixEnd="₸"
              value={cargoPrice}
              onChangeValue={setCargoPrice}
              isBgGray
              isDisabled={busy}
              errorText={cargoPrice !== "" && !validPrice ? t("expeditorInsurance.priceValidation") : ""}
            />
            <p className={style.tariff}>
              <HintIcon width={16} height={16} color="currentColor" aria-hidden="true" />
              {t("cargoInsurance.insuranceCostInfo3")}
            </p>
            <div
              className={style.upload}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                selectInvoice(event.dataTransfer.files?.[0]);
              }}
            >
              <UploadCloud size={28} />
              <div>
                <p>{invoice ? invoice.name : t("expeditorInsurance.uploadInvoice")}</p>
                {!invoice && (
                  <>
                    <button
                      type="button"
                      className={style.uploadLink}
                      disabled={busy}
                      onClick={() => fileInput.current?.click()}
                    >
                      {t("expeditorInsurance.chooseFile")}
                    </button>
                    <span> {t("expeditorInsurance.dropFile")}</span>
                  </>
                )}
              </div>
              {invoice && (
                <button
                  type="button"
                  className={style.iconButton}
                  disabled={busy}
                  aria-label={t("cargoInsurance.removeInvoice")}
                  onClick={() => selectInvoice(null)}
                >
                  <X size={18} />
                </button>
              )}
              <input
                ref={fileInput}
                className={style.hiddenInput}
                type="file"
                aria-label={t("expeditorInsurance.uploadInvoice")}
                disabled={busy}
                onChange={(event) => {
                  selectInvoice(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
            </div>
            <div className={style.notice}>
              <HintIcon aria-hidden="true" />
              <span>{t("cargoInsurance.availabilityNotice")}</span>
              <button type="button" className={style.textButton} onClick={() => setExclusionsOpen(true)}>
                {t("cargoInsurance.viewExclusions")}
              </button>
            </div>
            <dl className={style.summary}>
              <div>
                <dt>{t("expeditorInsurance.cargoPrice")}</dt>
                <dd>{formatInsuranceMoney(validPrice ? cargoPrice : 0)}</dd>
              </div>
              <div>
                <dt>{t("expeditorInsurance.insuranceSum")}</dt>
                <dd aria-live="polite">{quoteReady ? formatInsuranceMoney(quote.sum) : "—"}</dd>
              </div>
            </dl>
            {quoteError && (
              <div role="alert" className={style.error}>
                {t("expeditorInsurance.quoteError")}
                <button
                  type="button"
                  className={style.textButton}
                  onClick={() => setQuoteAttempt((value) => value + 1)}
                >
                  {t("common.retry")}
                </button>
              </div>
            )}
            {error && (
              <p role="alert" className={style.error}>
                {error}
              </p>
            )}
          </div>
          <div className={style.footer}>
            <CustomButton
              type="button"
              text={t("common.cancel")}
              isNavy
              isWhite
              onClick={onClose}
              disabled={busy}
              isDisabled={busy}
            />
            <CustomButton
              type="submit"
              text={t("common.confirm")}
              isNavy
              isLoading={busy}
              disabled={busy || !quoteReady || !invoice}
              isDisabled={busy || !quoteReady || !invoice}
            />
          </div>
        </form>
      </InsuranceModal>
      <InsuranceExclusionsModal isOpen={exclusionsOpen} onClose={() => setExclusionsOpen(false)} />
    </>
  );
}
